import { useState } from 'react';
import { Download, Share2, Printer, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import {
  regenerateInvoicePdf,
  shareInvoicePdf,
  base64ToBlob,
  downloadBlob,
} from '@/lib/api/invoice';
import type { AppTransaction } from '@/types/domain';

export interface PdfActionsProps {
  transaction: AppTransaction;
  variant?: 'default' | 'compact';
}

type ActionState = 'idle' | 'loading' | 'success' | 'error';

export function PdfActions({ transaction, variant = 'default' }: PdfActionsProps) {
  const [regenState, setRegenState] = useState<ActionState>('idle');
  const [shareState, setShareState] = useState<ActionState>('idle');
  const [error, setError] = useState<string | null>(null);

  const filename = `${transaction.invoiceNumber}.pdf`;

  const fetchPdf = async (): Promise<Blob> => {
    const res = await regenerateInvoicePdf(transaction.id);
    return base64ToBlob(res.pdfBase64);
  };

  const handleDownload = async () => {
    setRegenState('loading');
    setError(null);
    try {
      const blob = await fetchPdf();
      downloadBlob(blob, filename);
      setRegenState('success');
      setTimeout(() => setRegenState('idle'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal generate PDF');
      setRegenState('error');
    }
  };

  const handleShare = async () => {
    setShareState('loading');
    setError(null);
    try {
      const blob = await fetchPdf();
      const result = await shareInvoicePdf(
        blob,
        filename,
        `Invoice ${transaction.invoiceNumber}`,
        `Halo, berikut invoice ${transaction.invoiceNumber} dari kami.`,
      );
      if (result.method === 'cancelled') {
        setShareState('idle');
        return;
      }
      setShareState('success');
      setTimeout(() => setShareState('idle'), 2000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal share');
      setShareState('error');
    }
  };

  const handlePrint = () => {
    window.print();
  };

  const size = variant === 'compact' ? 'sm' : 'md';

  return (
    <div className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-2">
        <Button
          variant="primary"
          size={size}
          onClick={handleDownload}
          loading={regenState === 'loading'}
          leftIcon={
            regenState === 'loading' ? (
              <Loader2 size={16} className="animate-spin" />
            ) : regenState === 'success' ? (
              <RefreshCw size={16} />
            ) : (
              <Download size={16} />
            )
          }
        >
          {regenState === 'success' ? 'Downloaded' : 'Download PDF'}
        </Button>
        <Button
          variant="secondary"
          size={size}
          onClick={handleShare}
          loading={shareState === 'loading'}
          leftIcon={<Share2 size={16} />}
        >
          {shareState === 'success' ? 'Sent' : 'Share'}
        </Button>
        <Button
          variant="ghost"
          size={size}
          onClick={handlePrint}
          leftIcon={<Printer size={16} />}
          className="print:hidden"
        >
          Print
        </Button>
      </div>
      {error && <p className="text-caption text-danger">{error}</p>}
    </div>
  );
}
